// Capa fina WebMCP (navigator.modelContext) que expone la calculadora como
// "tools" invocables por agentes (Gemini en Chrome EPP, MCP-B, etc.) cuando
// el usuario tiene la pestaña abierta. Reusa la lógica pura de calculo.ts y
// el endpoint /api/combustible con su token de sesión, sin abrir una API
// pública nueva. Spec: https://webmachinelearning.github.io/webmcp/

import {
  calcularBase,
  calcularDeltaP,
  UMBRAL_AUTOMATICO_PCT,
  type Categoria,
} from './calculo.ts';
import { lunesPublicacion } from './fechas.ts';

export type TipoOperacion = 'urbano' | 'rigido' | 'tractora' | 'custom';

// Forma mínima del controlador AngularJS que las tools necesitan tocar.
// El resto de campos del vm real (precioFirma, costeKm, emisor…) no se
// modifican desde aquí: el agente prepara fechas/categoría/facturas y la
// UI Angular se encarga del resto al disparar cargarPrecios()/recalcular().
export interface VmShape {
  fechaFirma: Date | null;
  fechaEjecucion: Date | null;
  tipoOperacion: TipoOperacion;
  categoria: Categoria;
  gasoleoProf: boolean;
  esContinuado: boolean;
  facturas: Array<{ ref: string; importe: number | null }>;
}

export interface ToolDeps {
  vm: VmShape;
  // Ejecuta el callback dentro del ciclo digest de AngularJS (en producción
  // un wrapper sobre $timeout). Imprescindible: si el agente invoca la tool
  // desde fuera de Angular y mutamos vm directamente, la UI no refresca.
  aplicarMutacion: (fn: () => void) => void;
  // €/l (PAI+IH-GP) para la semana cuya publicación contiene el lunes dado,
  // o null si MITMS no devolvió precio. Reusa el token same-origin.
  pedirPrecioSemana: (lunesISO: string, gasoleoProf: boolean) => Promise<number | null>;
  cargarPrecios: () => void; // refresca firma+ejecución y al resolver llama a recalcular()
  recalcular: () => void;
  aplicarPreset: () => void; // aplica el preset según vm.tipoOperacion
}

interface ToolDescriptor {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (input: Record<string, unknown>) => unknown;
}

const CATEGORIA_ENUM: Categoria[] = ['mayor20', 'entre', 'obras', 'ligero'];
const TIPO_OPERACION_ENUM: TipoOperacion[] = ['urbano', 'rigido', 'tractora', 'custom'];

function round(n: number, decimals: number): number {
  const f = Math.pow(10, decimals);
  return Math.round(n * f) / f;
}

function asNumber(v: unknown): number | null {
  const n = typeof v === 'string' ? parseFloat(v) : (v as number);
  return typeof n === 'number' && isFinite(n) ? n : null;
}

export function crearTools(deps: ToolDeps): ToolDescriptor[] {
  return [
    {
      name: 'calcular-indexacion',
      description:
        'Calcula la revisión obligatoria del precio del transporte por carretera por variación del precio del gasóleo (RD-ley 9/2026, España). Devuelve la variación porcentual G, el coeficiente C de la tabla oficial, el ajuste ΔP y el importe final con desglose. No accede a precios oficiales: el llamador debe pasar precio_firma_eur_litro y precio_ejecucion_eur_litro (formato PAI+IH-GP del MITMS). Para resolver esos precios usa primero la tool obtener-precio-gasoleo.',
      inputSchema: {
        type: 'object',
        required: [
          'fecha_firma',
          'fecha_ejecucion',
          'precio_firma_eur_litro',
          'precio_ejecucion_eur_litro',
          'importe_sin_iva',
          'categoria',
        ],
        properties: {
          fecha_firma: { type: 'string', format: 'date', description: 'Fecha de firma del contrato (YYYY-MM-DD).' },
          fecha_ejecucion: { type: 'string', format: 'date', description: 'Fecha de ejecución del transporte (YYYY-MM-DD).' },
          precio_firma_eur_litro: {
            type: 'number', minimum: 0.1,
            description: 'Precio del gasóleo PAI+IH-GP la semana de la firma (€/l).',
          },
          precio_ejecucion_eur_litro: {
            type: 'number', minimum: 0.1,
            description: 'Precio del gasóleo PAI+IH-GP la semana de la ejecución (€/l).',
          },
          importe_sin_iva: { type: 'number', minimum: 0, description: 'Importe del transporte sin IVA (€).' },
          categoria: {
            enum: CATEGORIA_ENUM,
            description:
              'Categoría del vehículo: mayor20 (MMA ≥ 20 t), entre (3,5–20 t), obras (>3,5 t obras públicas), ligero (≤ 3,5 t).',
          },
          gasoleo_profesional: {
            type: 'boolean', default: false,
            description: 'Si el vehículo tiene derecho al IH profesional (≥ 7,5 t en censo). Solo cambia el cálculo desde el 01/07/2026.',
          },
          es_continuado: {
            type: 'boolean', default: false,
            description: 'Contrato de transporte continuado: la revisión aplica sin umbral del 5%.',
          },
        },
      },
      execute: (input) => {
        const fechaEjec = new Date(String(input['fecha_ejecucion']));
        const pf = asNumber(input['precio_firma_eur_litro']);
        const pe = asNumber(input['precio_ejecucion_eur_litro']);
        const imp = asNumber(input['importe_sin_iva']);
        const categoria = input['categoria'] as Categoria;
        if (pf == null || pe == null || imp == null || !CATEGORIA_ENUM.includes(categoria)) {
          return { error: 'Parámetros inválidos.' };
        }
        const base = calcularBase({
          precioFirma: pf,
          precioEjecucion: pe,
          fechaEjecucion: fechaEjec,
          categoria,
          gasoleoProf: !!input['gasoleo_profesional'],
        });
        const deltaP = calcularDeltaP(base.G_pct, imp, base.C);
        const continuado = !!input['es_continuado'];
        return {
          G_pct: round(base.G_pct, 4),
          PAI_eur_litro: round(base.PAI, 4),
          C: base.C,
          delta_P_eur: round(deltaP, 2),
          importe_original_eur: round(imp, 2),
          importe_final_eur: round(imp + deltaP, 2),
          aplica_revision: continuado || Math.abs(base.G_pct) >= UMBRAL_AUTOMATICO_PCT,
          umbral_automatico_pct: UMBRAL_AUTOMATICO_PCT,
        };
      },
    },

    {
      name: 'obtener-precio-gasoleo',
      description:
        'Obtiene el precio oficial del gasóleo PAI+IH-GP que el Ministerio de Transportes (MITMS) publica cada lunes. Devuelve €/l para la semana cuya publicación contiene la fecha indicada. Requiere que la pestaña esté abierta: la petición usa el token de sesión same-origin del usuario.',
      inputSchema: {
        type: 'object',
        required: ['fecha'],
        properties: {
          fecha: {
            type: 'string', format: 'date',
            description: 'Fecha cualquiera (YYYY-MM-DD); se redondea al lunes de publicación correspondiente.',
          },
          gasoleo_profesional: {
            type: 'boolean', default: false,
            description: 'Si el vehículo tiene derecho a IH-GP (≥ 7,5 t en censo). Solo cambia el cálculo desde el 01/07/2026.',
          },
        },
      },
      execute: async (input) => {
        const fecha = String(input['fecha'] ?? '');
        if (!fecha) return { error: 'Falta la fecha.' };
        const lunes = lunesPublicacion(new Date(fecha));
        const precio = await deps.pedirPrecioSemana(lunes, !!input['gasoleo_profesional']);
        if (precio == null) {
          return { error: 'El MITMS no publicó precio para la semana solicitada.' };
        }
        return { lunes_publicacion: lunes, precio_eur_litro: precio, formato: 'PAI+IH-GP' };
      },
    },

    {
      name: 'rellenar-formulario',
      description:
        'Pre-rellena el formulario de la calculadora con los datos indicados y dispara la consulta automática de precios al MITMS. La interfaz queda lista para que el usuario verifique visualmente y, si procede, descargue el justificante PDF. Útil cuando el agente quiere que el usuario revise el cálculo antes de finalizar.',
      inputSchema: {
        type: 'object',
        properties: {
          fecha_firma: { type: 'string', format: 'date' },
          fecha_ejecucion: { type: 'string', format: 'date' },
          tipo_operacion: {
            enum: TIPO_OPERACION_ENUM,
            description: 'Preset: urbano (furgoneta < 3,5 t sin IH-GP), rigido (3,5–20 t con IH-GP), tractora (≥ 20 t con IH-GP), custom (no toca categoría/IH).',
          },
          categoria: { enum: CATEGORIA_ENUM, description: 'Si se indica, sobrescribe el preset.' },
          gasoleo_profesional: { type: 'boolean' },
          es_continuado: { type: 'boolean' },
          facturas: {
            type: 'array', maxItems: 10,
            items: {
              type: 'object',
              required: ['importe'],
              properties: {
                referencia: { type: 'string' },
                importe: { type: 'number', minimum: 0 },
              },
            },
          },
        },
      },
      execute: (input) =>
        new Promise<{ ok: true }>((resolve) => {
          deps.aplicarMutacion(() => {
            const vm = deps.vm;
            const ff = input['fecha_firma'];
            const fe = input['fecha_ejecucion'];
            if (typeof ff === 'string' && ff) vm.fechaFirma = new Date(ff);
            if (typeof fe === 'string' && fe) vm.fechaEjecucion = new Date(fe);

            const tipo = input['tipo_operacion'];
            if (typeof tipo === 'string' && (TIPO_OPERACION_ENUM as string[]).includes(tipo)) {
              vm.tipoOperacion = tipo as TipoOperacion;
              if (tipo !== 'custom') deps.aplicarPreset();
            }

            // Overlay de specifics: priman sobre el preset cuando el agente
            // quiere afinar (ej. "tractora pero sin gasóleo profesional").
            const cat = input['categoria'];
            if (typeof cat === 'string' && (CATEGORIA_ENUM as string[]).includes(cat)) {
              vm.categoria = cat as Categoria;
            }
            if (typeof input['gasoleo_profesional'] === 'boolean') {
              vm.gasoleoProf = input['gasoleo_profesional'] as boolean;
            }
            if (typeof input['es_continuado'] === 'boolean') {
              vm.esContinuado = input['es_continuado'] as boolean;
            }

            const facturas = input['facturas'];
            if (Array.isArray(facturas) && facturas.length) {
              vm.facturas = facturas.slice(0, 10).map((f) => {
                const obj = f as Record<string, unknown>;
                return {
                  ref: typeof obj['referencia'] === 'string' ? (obj['referencia'] as string) : '',
                  importe: asNumber(obj['importe']),
                };
              });
            }

            deps.cargarPrecios();
            deps.recalcular();
            resolve({ ok: true });
          });
        }),
    },
  ];
}

declare global {
  interface Navigator {
    readonly modelContext?: {
      provideContext: (cfg: { tools: unknown[] }) => void;
    };
  }
}

export function registrarMcp(deps: ToolDeps): boolean {
  if (typeof navigator === 'undefined') return false;
  const ctx = navigator.modelContext;
  if (!ctx || typeof ctx.provideContext !== 'function') return false;
  try {
    ctx.provideContext({ tools: crearTools(deps) });
    return true;
  } catch {
    return false;
  }
}
